<template>
	<Modal
		v-if="isOpen"
		:allow-close="false"
		:hide-actions="true"
		:remove-padding="true"
		max-width="sm:max-w-[400px]"
		rounded-size="rounded-lg"
	>
		<template #body>
			<div class="p-6">
				<h2 class="text-[18px] leading-normal font-bold text-won-dark-blue">
					Did you apply?
				</h2>

				<p class="mt-1.5 text-sm leading-normal text-gray-500">
					We'll add it to your Job Tracker so you never lose track of where you stand.
				</p>

				<!-- Not yet comes first so Tab follows the desktop row; phones stack Yes on top, full width. -->
				<div class="mt-6 flex justify-end gap-2 max-sm:flex-col-reverse">
					<button
						type="button"
						class="
							h-10 rounded-lg bg-[#F4F4F5] px-5 text-sm font-medium text-won-dark-blue
							transition hover:bg-[#E4E4E7]
						"
						@click="answerNo"
					>
						Not yet
					</button>

					<button
						type="button"
						class="
							h-10 rounded-lg bg-won-green px-5 text-sm font-semibold text-white
							transition hover:bg-won-green-dark
						"
						@click="answerYes"
					>
						Yes, add to JobTrackerAI
					</button>
				</div>
			</div>
		</template>
	</Modal>
</template>

<script setup>
import { computed, ref, watch, onMounted, onBeforeUnmount } from 'vue';
import { storeToRefs } from 'pinia';

import Modal from '../modal.vue';
import { useApplyConfirmationStore } from '../../store/apply-confirmation.store';
import { useJobBoardStore } from '../../store/job-board.store';
import { useUserEvents, USER_EVENTS } from '../../store/user-events.store';

/**
 * EN-621 — asks whether the seeker submitted the application we just sent them off to.
 *
 * Mounted globally rather than on the job board, because the redirect opens a new tab and the
 * seeker may have navigated elsewhere in the SPA tab before coming back.
 */

/**
 * `visibilitychange` fires just before `focus` on a tab switch, so both handlers would
 * otherwise open the prompt twice; one frame of debounce collapses the pair.
 */
const RETURN_DEBOUNCE_MS = 150;

const store = useApplyConfirmationStore();
const { current } = storeToRefs(store);

const armed = ref(false);
let returnTimer = null;

const isOpen = computed(() => armed.value && current.value !== null);

/**
 * The seeker left the tab, so every queued redirect really did open — this is what stops a
 * popup blocked by the browser from producing a prompt for a job they never saw.
 */
const onDepart = () => {
	// An open prompt stays open across a tab switch. Disarming it here would close and then
	// reopen it on return, which flickers and counts a second `shown`.
	if (!isOpen.value) {
		armed.value = false;
	}

	store.markDeparted();
};

const onReturn = () => {
	if (document.visibilityState !== 'visible' || isOpen.value) {
		return;
	}

	clearTimeout(returnTimer);
	returnTimer = setTimeout(() => {
		store.prune();

		if (store.current !== null) {
			armed.value = true;
		}
	}, RETURN_DEBOUNCE_MS);
};

const onVisibilityChange = () => (document.hidden ? onDepart() : onReturn());

// Escape is the only way out other than the two buttons: the design has no close icon.
const onKeydown = (event) => {
	if (event.key === 'Escape' && isOpen.value) {
		answerNo();
	}
};

const answerYes = () => {
	const { providerJobId } = current.value;

	useUserEvents().trackEvent(USER_EVENTS.APPLY_CONFIRM_YES, { provider_job_id: providerJobId });

	// Not awaited: the store drops the entry synchronously, which is what closes this prompt
	// or advances to the next one. Waiting on the request would only leave the next prompt
	// looking disabled until it resolved, and the store swallows the failure case anyway.
	//
	// Once the server has it, the job board drops the job the way it does after AutoApply.
	// Called from here rather than the store: auth.js imports that store, and the job board
	// store's import graph calls setAuthState() at load, so importing it there is circular.
	store.confirm(providerJobId).then((confirmed) => {
		if (confirmed) {
			useJobBoardStore().markJobAppliedLocally(providerJobId);
		}
	});
};

const answerNo = () => {
	const { providerJobId } = current.value;

	useUserEvents().trackEvent(USER_EVENTS.APPLY_CONFIRM_NO, { provider_job_id: providerJobId });
	store.dismiss(providerJobId);
};

// Each queued job gets its own prompt, in the order they were opened, so this fires again as
// the queue advances rather than only on the first one.
watch(
	() => (isOpen.value ? current.value?.providerJobId : null),
	(providerJobId) => {
		if (providerJobId != null) {
			useUserEvents().trackEvent(USER_EVENTS.APPLY_CONFIRM_SHOWN, { provider_job_id: providerJobId });
		}
	},
);

onMounted(() => {
	window.addEventListener('focus', onReturn);
	window.addEventListener('blur', onDepart);
	window.addEventListener('pageshow', onReturn);
	document.addEventListener('visibilitychange', onVisibilityChange);
	document.addEventListener('keydown', onKeydown);
});

onBeforeUnmount(() => {
	clearTimeout(returnTimer);
	window.removeEventListener('focus', onReturn);
	window.removeEventListener('blur', onDepart);
	window.removeEventListener('pageshow', onReturn);
	document.removeEventListener('visibilitychange', onVisibilityChange);
	document.removeEventListener('keydown', onKeydown);
});
</script>
